<template>
  <div class="simple-pdf-viewer">
    <nav-bar
        fixed
        z-index="99"
        title="合同查看"
        left-text="返回"
        left-arrow
        @click-left="$router.go(-1)"
    >
      <!-- 右侧操作按钮 -->
      <template #right>
        <div class="nav-right-buttons">
          <van-button type="primary" size="small" @click="downloadPdf" class="download-btn">下载PDF</van-button>
        </div>
      </template>
    </nav-bar>
    <div style="height: 46px"></div>

    <!-- PDF.js渲染区域 -->
    <div v-if="!loading && !loadError" ref="pdfContainer" class="pdf-container">
      <div v-for="n in numPages" :key="n" :id="`pdf-page-${n}`" class="pdf-page"></div>
    </div>

    <!-- 加载中提示 -->
    <div v-if="loading" class="loading-container">
      <van-loading type="spinner" color="#0ce6df" />
      <p>加载中...</p>
    </div>

    <!-- 加载失败提示 -->
    <div v-if="loadError" class="error-container">
      <p>{{ errorMessage }}</p>
      <div class="error-buttons">
        <van-button type="primary" size="small" @click="openInNewTab">在浏览器中打开</van-button>
        <van-button plain size="small" @click="retryLoading">重试</van-button>
      </div>
    </div>

    <!-- 下载成功提示 -->
    <van-toast id="van-toast" />
  </div>
</template>

<script>
import { NavBar, Loading, Button, Toast, Icon } from 'vant';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker';

export default {
  name: 'SimplePdfViewer',
  components: {
    NavBar,
    VanLoading: Loading,
    VanButton: Button,
    VanIcon: Icon
  },
  data() {
    return {
      pdfUrl: '',
      loading: true,
      loadError: false,
      errorMessage: '无法加载PDF文件，请尝试其他方式查看',
      pdf: null,
      numPages: 0,
      currentPage: 1,
      pdfLib: pdfjsLib,
      scale: 1,
      originalPdfUrl: ''
    };
  },
  created() {
    this.initPdfViewer();
  },
  mounted() {
    // 设置worker路径
    this.pdfLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';
  },
  methods: {
    initPdfViewer() {
      const urlParam = this.$route.query.url;
      if (!urlParam) {
        this.loading = false;
        this.loadError = true;
        this.errorMessage = '未找到合同地址';
        return;
      }

      try {
        // 解码PDF URL
        this.pdfUrl = decodeURIComponent(urlParam);
        console.log('解码后的PDF URL:', this.pdfUrl);
        // 协议强制转换
        this.originalUrl = decodeURIComponent(urlParam).replace(/^http:/, 'https:');
        this.pdfUrl = this.originalUrl;
        this.originalPdfUrl = this.originalUrl; // 新增：始终保存原始URL

        // 先尝试直接获取PDF内容检查
        this.checkPdfContent(this.pdfUrl).then(isValid => {
          if (isValid) {
            // 内容有效，使用PDF.js加载PDF
            this.renderPdf();
          } else {
            // 内容无效，显示错误
            this.loading = false;
            this.loadError = true;
            this.errorMessage = '获取到的内容不是有效的PDF格式，可能是代理接口返回了错误信息';

            // 尝试直接使用原始URL
            if (this.pdfUrl.includes('/api/pdf/proxy')) {
              try {
                const urlParams = new URLSearchParams(this.pdfUrl.split('?')[1]);
                const originalUrl = urlParams.get('url');
                if (originalUrl) {
                  const decodedOriginalUrl = decodeURIComponent(originalUrl);
                  console.log('尝试直接使用原始URL:', decodedOriginalUrl);
                  this.originalPdfUrl = decodedOriginalUrl;

                  // 提示用户尝试直接查看原始URL
                  this.errorMessage += '\n\n您可以尝试直接在浏览器中打开原始链接';

                  // 添加打开按钮
                  this.$nextTick(() => {
                    const errorContainer = document.querySelector('.error-container');
                    if (errorContainer) {
                      const openButton = document.createElement('button');
                      openButton.innerText = '在浏览器中打开原始链接';
                      openButton.className = 'open-button';
                      openButton.onclick = () => window.open(decodedOriginalUrl, '_blank');
                      errorContainer.appendChild(openButton);
                    }
                  });
                }
              } catch (e) {
                console.error('解析原始URL失败:', e);
              }
            }
          }
        }).catch(error => {
          console.error('检查PDF内容失败:', error);
          this.loading = false;
          this.loadError = true;
          this.errorMessage = `检查PDF内容失败: ${error.message}`;
        });
      } catch (error) {
        console.error('PDF预览初始化失败:', error);
        this.loading = false;
        this.loadError = true;
        this.errorMessage = `初始化失败: ${error.message}`;
      }
    },

    // 检查PDF内容是否有效
    checkPdfContent(url) {
      return new Promise((resolve, reject) => {
        fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/pdf,*/*',
            'Cache-Control': 'no-cache'
          },
          cache: 'no-store'
        })
            .then(response => {
              console.log('PDF内容响应状态:', response.status, response.statusText);
              console.log('PDF内容类型:', response.headers.get('content-type'));

              if (!response.ok) {
                throw new Error(`网络响应异常: ${response.status}`);
              }

              return response.arrayBuffer();
            })
            .then(buffer => {
              // 检查PDF文件头 (%PDF-)
              const header = new Uint8Array(buffer.slice(0, 5));
              const headerStr = String.fromCharCode.apply(null, header);
              console.log('PDF文件头:', headerStr, Array.from(header));

              const isValid = headerStr.startsWith('%PDF-');

              if (!isValid) {
                // 如果不是PDF，尝试以文本形式查看内容
                const text = new TextDecoder().decode(buffer.slice(0, 200));
                console.error('内容不是PDF，前200个字符:', text);
              }

              resolve(isValid);
            })
            .catch(error => {
              console.error('获取PDF内容失败:', error);
              reject(error);
            });
      });
    },

    renderPdf() {
      if (!this.pdfLib) {
        this.loadError = true;
        this.loading = false;
        this.errorMessage = 'PDF.js库未加载';
        return;
      }

      console.log('开始加载PDF:', this.pdfUrl);

      // 使用PDF.js加载并渲染PDF
      const loadingTask = this.pdfLib.getDocument({
        url: this.pdfUrl,
        withCredentials: false, // 不发送凭证
        cMapUrl: window.location.origin + '/cmaps/',
        cMapPacked: true,
        // 添加更多参数以便调试
        verbosity: 1, // 增加日志级别
        isEvalSupported: true,
        disableAutoFetch: false,
        disableStream: false,
        disableRange: false
      });

      // 添加进度监听
      if (loadingTask.onProgress) {
        loadingTask.onProgress = (progressData) => {
          console.log('PDF加载进度:', progressData.loaded, '/', progressData.total);
        };
      }

      loadingTask.promise.then(pdf => {
        console.log('PDF加载成功，页数:', pdf.numPages);
        this.pdf = pdf;
        this.numPages = pdf.numPages;
        this.loading = false;

        // 渲染所有页面
        for (let i = 1; i <= pdf.numPages; i++) {
          this.renderPage(i);
        }
      }).catch(error => {
        // 使用handleLoadError方法处理错误
        this.handleLoadError(error);
      });
    },

    renderPage(pageNumber) {
      this.pdf.getPage(pageNumber).then(page => {
        const viewport = page.getViewport({scale: this.scale});
        const container = document.getElementById(`pdf-page-${pageNumber}`);

        if (!container) return;

        // 创建canvas元素
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // 设置canvas样式
        canvas.style.width = '100%';
        canvas.style.height = 'auto';

        // 清空容器并添加canvas
        container.innerHTML = '';
        container.appendChild(canvas);

        // 渲染PDF页面到canvas
        const renderContext = {
          canvasContext: context,
          // 关键：启用注释和交互式表单渲染
          renderInteractiveForms: true,
          annotationMode: 1, // 启用签章
          textLayer: false, // 避免文本层遮挡
          viewport: viewport
        };

        page.render(renderContext);
      });
    },

    openInNewTab() {
      // 在新标签页中打开PDF
      let url = this.pdfUrl;

      // 如果有originalPdfUrl，优先使用它
      if (this.originalPdfUrl) {
        url = this.originalPdfUrl;
      }
      // 否则尝试从代理URL中提取原始URL
      else if (this.pdfUrl && this.pdfUrl.includes('/api/pdf/proxy')) {
        try {
          const urlParams = new URLSearchParams(this.pdfUrl.split('?')[1]);
          const originalUrl = urlParams.get('url');
          if (originalUrl) {
            url = decodeURIComponent(originalUrl);
          }
        } catch (error) {
          console.error('解析原始URL失败:', error);
        }
      }

      if (url) {
        window.open(url, '_blank');
        this.$toast('已在浏览器中打开');
      } else {
        this.$dialog.alert({
          title: '提示',
          message: '链接无效'
        });
      }
    },

    downloadPdf() {
      if (this.pdfUrl) {
        Toast.loading({
          message: '准备下载...',
          forbidClick: true,
          duration: 0 // 不自动关闭
        });

        const downloadUrl = this.originalPdfUrl; // 直接使用原始URL

        // 使用fetch API下载PDF
        fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/pdf',
            'Cache-Control': 'no-cache'
          },
          cache: 'no-store'
        })
            .then(response => {
              console.log('下载响应状态:', response.status, response.statusText);
              console.log('下载响应类型:', response.headers.get('content-type'));

              if (!response.ok) {
                throw new Error(`网络响应异常: ${response.status}`);
              }
              return response.blob();
            })
            .then(blob => {
              console.log('下载的Blob类型:', blob.type, '大小:', blob.size);
              console.log('下载的路径:', downloadUrl);


              // 检查blob类型
              if (blob.type !== 'application/pdf' && blob.size < 1000) {
                // 可能不是PDF，尝试读取内容
                return blob.text().then(text => {
                  console.log('下载的内容不是PDF:', text);
                  throw new Error('下载的内容不是有效的PDF');
                });
              }

              // 创建Blob URL
              const blobUrl = downloadUrl;

              // 创建下载链接
              const a = document.createElement('a');
              a.style.display = 'none';
              a.href = downloadUrl;
              console.log('创建下载链接', downloadUrl);
              // 从URL中提取文件名
              let filename = 'contract.pdf';
              try {
                if (downloadUrl) {
                  const urlParts = downloadUrl.split('/');
                  const lastPart = urlParts[urlParts.length - 1];
                  if (lastPart && lastPart.toLowerCase().endsWith('.pdf')) {
                    filename = lastPart;
                    console.log('提取到的文件名:', filename);
                  }
                }
              } catch (error) {
                console.error('提取文件名失败:', error);
              }

              a.download = filename;
              console.log('下载的a标签地址是',  a.download);
              // 添加到DOM，触发点击，然后移除
              document.body.appendChild(a);
              a.click();

              // 清理
              window.URL.revokeObjectURL(blobUrl);
              document.body.removeChild(a);

              // 显示下载成功提示
              Toast.clear();
              Toast.success('下载成功');
              //将路径打印出来
              console.log('PDF下载成功，路径:', downloadUrl);
            })
            .catch(error => {
              console.error('下载PDF失败:', error);
              Toast.clear();
              Toast.fail('下载失败，请重试');

              // 如果fetch失败，尝试直接打开PDF
              if (downloadUrl && downloadUrl !== this.pdfUrl) {
                console.log('尝试直接打开原始URL:', downloadUrl);
                setTimeout(() => {
                  window.open(downloadUrl, '_blank');
                }, 1500);
              } else {
                setTimeout(() => {
                  window.open(this.pdfUrl, '_blank');
                }, 1500);
              }
            });
      } else {
        Toast.fail('未找到PDF文件');
      }
    },

    retryLoading() {
      this.loading = true;
      this.loadError = false;
      this.initPdfViewer();
    },

    // 添加新方法，尝试直接下载PDF
    tryDirectDownload() {
      if (!this.pdfUrl) return;

      console.log('尝试直接下载PDF而不是预览');

      // 显示提示
      Toast({
        message: '正在尝试直接下载PDF...',
        position: 'bottom',
        duration: 3000
      });

      // 延迟执行下载
      setTimeout(() => {
        this.downloadPdf();
      }, 2000);
    },

    // 添加新方法，用于直接打开原始PDF
    openOriginalPdf(url) {
      if (!url) return;

      // 在新窗口中打开原始PDF
      window.open(url, '_blank');
      Toast('已在新窗口中打开原始PDF');
    },

    // 添加新方法，用于处理加载失败的情况
    handleLoadError(error) {
      console.error('加载PDF失败:', error);
      this.loading = false;
      this.loadError = true;

      // 提供更详细的错误信息
      if (error.name === 'MissingPDFException') {
        this.errorMessage = '找不到PDF文件，请检查URL是否正确';

        // 尝试从代理URL中提取原始URL
        if (this.pdfUrl.includes('/api/pdf/proxy')) {
          try {
            const urlParams = new URLSearchParams(this.pdfUrl.split('?')[1]);
            const originalUrl = urlParams.get('url');
            if (originalUrl) {
              const decodedOriginalUrl = decodeURIComponent(originalUrl);
              console.log('尝试直接使用原始URL:', decodedOriginalUrl);

              // 设置原始URL，用于后续操作
              this.originalPdfUrl = decodedOriginalUrl;

              // 添加到错误信息中
              this.errorMessage += '\n\n您可以尝试直接在浏览器中打开原始链接';
            }
          } catch (e) {
            console.error('解析原始URL失败:', e);
          }
        }
      } else if (error.name === 'InvalidPDFException') {
        this.errorMessage = '无效的PDF文件格式，可能是跨域问题导致';

        // 尝试直接下载PDF而不是预览
        this.tryDirectDownload();
      } else if (error.name === 'UnexpectedResponseException') {
        this.errorMessage = `服务器返回异常：${error.status}`;
      } else if (error.message) {
        this.errorMessage = `加载失败：${error.message}`;
      } else {
        this.errorMessage = '无法加载PDF文件，请检查网络连接';
      }

      // 如果是跨域问题，提示用户
      if (error.name === 'InvalidPDFException' || error.name === 'UnexpectedResponseException') {
        this.errorMessage += '\n\n这可能是由于跨域问题导致，请尝试在浏览器中打开或下载查看';

        // 添加打开按钮
        this.$nextTick(() => {
          const errorContainer = document.querySelector('.error-container');
          if (errorContainer) {
            const openButton = document.createElement('button');
            openButton.innerText = '在浏览器中打开';
            openButton.className = 'open-button';
            openButton.onclick = () => this.openInNewTab();
            errorContainer.appendChild(openButton);
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.simple-pdf-viewer {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5;
}

.nav-right-buttons {
  display: flex;
  align-items: center;
  padding-right: 10px;
}

.download-btn {
  background-color: #0ce6df;
  border: none;
  color: white;
  font-weight: bold;
  border-radius: 4px;
  padding: 0 15px;
}

.pdf-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  background-color: #f5f5f5;
}

.pdf-page {
  margin: 10px auto;
  background-color: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  max-width: 100%;
}

.loading-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.loading-container p {
  margin-top: 10px;
  color: #666;
}

.error-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #666;
  width: 80%;
}

.error-container p {
  margin-bottom: 15px;
}

.error-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
}
</style>
